import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, MapPin, Loader2, ArrowRight } from "lucide-react";
import { useBranchModal } from "@/contexts/BranchModalContext";
import { useBrand } from "@/contexts/BrandContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCustomer } from "@/hooks/useCustomer";
import { useSessionBranch } from "@/hooks/useSessionBranch";
import { useSessionContext } from "@/contexts/SessionContext";
import { useCart } from "@/contexts/CartContext";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import ChangeConfirmDialog from "./ChangeConfirmDialog";

const GOOGLE_MAPS_API_KEY = "AIzaSyCUM11R3zDdCrA6Ijlnys02Be_9IXsulRE";

interface AddressResult {
  formatted_address: string;
  street: string;
  building: string;
  area: string;
  city: string;
  country: string;
  lat: number;
  lng: number;
}

interface PendingBranchData {
  branchId: string;
  name: string;
  estimatedPrepTime: number;
  distanceKm: number;
  lat: number;
  lng: number;
}

let mapsLoaded = false;
let mapsLoadPromise: Promise<void> | null = null;
function loadGoogleMaps(): Promise<void> {
  if (mapsLoaded && (window as any).google?.maps) return Promise.resolve();
  if (mapsLoadPromise) return mapsLoadPromise;
  mapsLoadPromise = new Promise((resolve, reject) => {
    const callbackName = `__gmapsInit_${Date.now()}`;
    (window as any)[callbackName] = () => {
      mapsLoaded = true;
      delete (window as any)[callbackName];
      resolve();
    };
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onerror = (e) => {
      mapsLoadPromise = null;
      delete (window as any)[callbackName];
      reject(e);
    };
    document.head.appendChild(script);
  });
  return mapsLoadPromise;
}

const DEFAULT_CENTER = { lat: 24.7136, lng: 46.6753 };

export default function DeliveryMapStep() {
  const { goBack, closeBranchModal, onCompleteRef } = useBranchModal();
  const { brand } = useBrand();
  const { t, language } = useLanguage();
  const isRTL = language === "ar";
  const { customer, setSelectedAddress, setLastCoordinates } = useCustomer();
  const { setBranch } = useSessionBranch();
  const { session, setOrderType, setDistance } = useSessionContext();
  const { clearCart, itemsCount } = useCart();

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);

  const [loading, setLoading] = useState(true);
  const [mapError, setMapError] = useState<string | null>(null);
  const [geocoding, setGeocoding] = useState(false);
  const [address, setAddress] = useState<AddressResult | null>(null);
  const [notes, setNotes] = useState("");
  const [noBranchError, setNoBranchError] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [pendingBranch, setPendingBranch] = useState<PendingBranchData | null>(null);

  const initialCenter =
    customer?.lastCoordinates?.latitude && customer?.lastCoordinates?.longitude
      ? {
          lat: customer.lastCoordinates.latitude,
          lng: customer.lastCoordinates.longitude,
        }
      : DEFAULT_CENTER;

  const reverseGeocode = useCallback(
    async (lat: number, lng: number) => {
      setGeocoding(true);
      try {
        if (!window.google?.maps || !window.google?.maps.places) {
          throw new Error("Google Maps JS API or Places library not loaded");
        }
        const mapDiv = document.createElement("div");
        const map = new window.google.maps.Map(mapDiv);
        const service = new window.google.maps.places.PlacesService(map);
        const place = await new Promise<google.maps.places.PlaceResult>(
          (resolve, reject) => {
            service.nearbySearch(
              { location: { lat, lng }, radius: 10 },
              (results, status) => {
                if (
                  status === window.google.maps.places.PlacesServiceStatus.OK &&
                  results?.length
                ) {
                  resolve(results[0]);
                } else {
                  reject(new Error(`Places nearbySearch failed: ${status}`));
                }
              },
            );
          },
        );
        if (!place.place_id) throw new Error("Place ID not found");
        const details = await new Promise<google.maps.places.PlaceResult>(
          (resolve, reject) => {
            service.getDetails(
              {
                placeId: place.place_id,
                fields: ["address_components", "formatted_address"],
              },
              (placeResult, status) => {
                if (
                  status === window.google.maps.places.PlacesServiceStatus.OK &&
                  placeResult
                ) {
                  resolve(placeResult);
                } else {
                  reject(new Error(`Places getDetails failed: ${status}`));
                }
              },
            );
          },
        );
        const components = details.address_components || [];
        const get = (type: string) =>
          components.find((c) => c.types.includes(type))?.long_name || "";
        setAddress({
          formatted_address:
            details.formatted_address || `${get("route")}, ${get("locality")}`,
          street: get("route"),
          building: get("street_number"),
          area:
            get("sublocality_level_1") || get("sublocality") || get("locality"),
          city: get("administrative_area_level_1"),
          country: get("country"),
          lat,
          lng,
        });
      } catch (err) {
        console.error("Reverse geocoding failed:", err);
      } finally {
        setGeocoding(false);
      }
    },
    [language],
  );

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(() => {
        if (cancelled || !mapRef.current) return;
        const map = new (window as any).google.maps.Map(mapRef.current, {
          center: initialCenter,
          zoom: 15,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "greedy",
          styles: [{ featureType: "poi", stylers: [{ visibility: "off" }] }],
        });
        mapInstanceRef.current = map;
        const marker = new (window as any).google.maps.Marker({
          position: initialCenter,
          map,
          draggable: true,
          animation: (window as any).google.maps.Animation.DROP,
        });
        markerRef.current = marker;
        marker.addListener("dragend", () => {
          const pos = marker.getPosition();
          if (pos) reverseGeocode(pos.lat(), pos.lng());
        });
        map.addListener("click", (e: any) => {
          if (e.latLng) {
            marker.setPosition(e.latLng);
            reverseGeocode(e.latLng.lat(), e.latLng.lng());
          }
        });
        setLoading(false);
        reverseGeocode(initialCenter.lat, initialCenter.lng);
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
          setMapError(
            "Failed to load map. Please check your API key restrictions include this domain.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        if (mapInstanceRef.current && markerRef.current) {
          mapInstanceRef.current.panTo(coords);
          markerRef.current.setPosition(coords);
          reverseGeocode(coords.lat, coords.lng);
        }
      },
      () => {},
      { enableHighAccuracy: true, timeout: 5000 },
    );
  }, [reverseGeocode]);

  // Applies the branch after optional cart reset
  const applyBranch = useCallback(
    async (pending: PendingBranchData, shouldResetCart: boolean) => {
      if (shouldResetCart) await clearCart();
      setOrderType("delivery", brand.productsAvailableInAllBranches);
      setDistance(pending.distanceKm);
      setBranch(
        pending.branchId,
        pending.name,
        pending.estimatedPrepTime,
        0,
        brand.productsAvailableInAllBranches,
      );
      setNoBranchError(false);
      onCompleteRef.current?.({
        lat: pending.lat,
        lon: pending.lng,
        branchId: pending.branchId,
      });
      closeBranchModal();
    },
    [
      clearCart,
      setOrderType,
      setDistance,
      setBranch,
      brand.productsAvailableInAllBranches,
      onCompleteRef,
      closeBranchModal,
    ],
  );

  const handleConfirm = async () => {
    if (!address) return;
    const pos = markerRef.current?.getPosition();
    if (!pos) return;

    setConfirming(true);

    setLastCoordinates(pos.lat(), pos.lng());
    setSelectedAddress({
      latitude: pos.lat(),
      longitude: pos.lng(),
      streetName: address.street || null,
      buildingNumber: address.building || null,
      floorNumber: null,
      apartmentNumber: null,
      geocodeAddress: address.formatted_address,
      details: notes || null,
    });

    try {
      const { data, error } = await supabase.functions.invoke("nearest-branch", {
        body: { lat: pos.lat(), lng: pos.lng(), restaurant_id: brand.id },
      });

      if (!error && data?.branch_id) {
        const pending: PendingBranchData = {
          branchId: data.branch_id,
          name: data.name || "",
          estimatedPrepTime: data.estimated_prep_time ?? 0,
          distanceKm: data.distance_km ?? 0,
          lat: pos.lat(),
          lng: pos.lng(),
        };

        const isChangingBranch =
          session.order.branchId && session.order.branchId !== data.branch_id;
        const shouldConfirmClear =
          itemsCount > 0 &&
          isChangingBranch &&
          !brand.productsAvailableInAllBranches;

        setConfirming(false);

        if (shouldConfirmClear) {
          setPendingBranch(pending);
               return;
        }

        await applyBranch(pending, false);
        return;
      } else {
        setConfirming(false);
        setNoBranchError(true);
        return;
      }
    } catch {
      // fall through
    }

    setConfirming(false);
    onCompleteRef.current?.({ lat: pos.lat(), lon: pos.lng() });
    closeBranchModal();
  };

  return (
    <div className="flex flex-col h-[80vh] max-h-[80vh]">
      <div className="relative flex items-center justify-center px-4 py-3 border-b border-border shrink-0 min-h-[64px] bg-background">
        <DialogHeader className="pointer-events-none">
          <DialogTitle className="text-base sm:text-lg font-semibold text-center">
            {t("branch.selectLocation")}
          </DialogTitle>
        </DialogHeader>
        <Button
          variant="ghost"
          size="icon"
          onClick={goBack}
          className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full hover:bg-muted"
        >
          {isRTL ? (
            <ArrowRight className="h-5 w-5" />
          ) : (
            <ArrowLeft className="h-5 w-5" />
          )}
        </Button>
      </div>

      <div className="relative flex-1 min-h-0">
        <div ref={mapRef} className="w-full h-full" />
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}
        {mapError && (
          <div className="absolute inset-0 flex items-center justify-center bg-background p-6">
            <div className="text-center space-y-3">
              <MapPin className="h-10 w-10 text-destructive mx-auto" />
              <p className="text-sm text-muted-foreground">{mapError}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setMapError(null);
                  setLoading(true);
                  mapsLoadPromise = null;
                  mapsLoaded = false;
                  loadGoogleMaps()
                    .then(() => {
                      if (!mapRef.current) return;
                      const map = new (window as any).google.maps.Map(
                        mapRef.current,
                        {
                          center: initialCenter,
                          zoom: 15,
                          disableDefaultUI: true,
                          zoomControl: true,
                          gestureHandling: "greedy",
                        },
                      );
                      mapInstanceRef.current = map;
                      const marker = new (window as any).google.maps.Marker({
                        position: initialCenter,
                        map,
                        draggable: true,
                      });
                      markerRef.current = marker;
                      marker.addListener("dragend", () => {
                        const p = marker.getPosition();
                        if (p) reverseGeocode(p.lat(), p.lng());
                      });
                      map.addListener("click", (e: any) => {
                        if (e.latLng) {
                          marker.setPosition(e.latLng);
                          reverseGeocode(e.latLng.lat(), e.latLng.lng());
                        }
                      });
                      setLoading(false);
                      reverseGeocode(initialCenter.lat, initialCenter.lng);
                    })
                    .catch(() => {
                      setLoading(false);
                      setMapError("Still unable to load the map.");
                    });
                }}
              >
                Retry
              </Button>
            </div>
          </div>
        )}
        <button
          onClick={() => {
            if (!navigator.geolocation) return;
            navigator.geolocation.getCurrentPosition((pos) => {
              const coords = {
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
              };
              mapInstanceRef.current?.panTo(coords);
              markerRef.current?.setPosition(coords);
              reverseGeocode(coords.lat, coords.lng);
            });
          }}
          className="absolute bottom-3 right-3 z-10 bg-background rounded-full p-2 shadow-md border border-border hover:bg-muted transition-colors"
          title={t("branch.myLocation")}
        >
          <MapPin className="h-5 w-5 text-primary" />
        </button>
      </div>

      <div className="p-4 border-t border-border space-y-3 shrink-0">
        {geocoding ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("branch.detectingAddress")}
          </div>
        ) : address ? (
          <div>
            <p className="text-sm font-medium text-foreground line-clamp-2">
              {address.formatted_address}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {address.area}
              {address.city ? `, ${address.city}` : ""}
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t("branch.dragPin")}</p>
        )}

        {noBranchError && (
          <div className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-center space-y-0.5">
            <p className="text-sm font-medium text-destructive">
              {t("branch.noBranchError")}
            </p>
          </div>
        )}
        <Input
          placeholder={t("branch.deliveryNotes")}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="text-sm"
        />
        <Button
          className="w-full"
          disabled={!address || geocoding || confirming}
          onClick={handleConfirm}
        >
          {confirming ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("branch.confirmLocation")}
            </span>
          ) : (
            t("branch.confirmLocation")
          )}
        </Button>
      </div>

      {/* Branch change confirmation dialog */}
      <ChangeConfirmDialog
        open={!!pendingBranch}
        itemsCount={itemsCount}
        branchName={session.order.branchName ?? ""}
        onKeepCurrent={() => {
          setPendingBranch(null);
          closeBranchModal();
        }}
        onSwitch={() => {
          if (pendingBranch) applyBranch(pendingBranch, true);
          setPendingBranch(null);
        }}
      />
    </div>
  );
}